import accessUtil from './accessUtil';

const readerCapStatuses = ['APPROVED', 'ACCEPTED', 'SENT', 'CREATED'];

// Prefer refreshed profile data for the book's account over the selected account.
// Book collaborators without an account membership use the limit returned by the API.
const getMaxReadersPerBook = ({
  activeAccount,
  userProfile = {},
  currentBook = {},
}) => {
  if (currentBook.maxReadersPerBookOverride != null) {
    return currentBook.maxReadersPerBookOverride;
  }
  const accountId = currentBook.account?._id || currentBook.account;
  if (!accountId) {
    return activeAccount?.maxReadersPerBook;
  }
  const accounts = [
    userProfile?.account,
    ...(userProfile?.accountMembership || []).map(
      membership => membership.account
    ),
    activeAccount,
  ];
  const bookAccount = accounts.find(account => account?._id === accountId);
  return bookAccount?.maxReadersPerBook ?? currentBook.maxReadersPerBook;
};

/**
 * @param {{activeAccount?: any, userProfile?: any, readers?: any[], activeReaders?: number, currentBook?: any}} options
 */
const canAddMoreReaders = ({
  activeAccount,
  userProfile = {},
  readers = [],
  activeReaders = undefined,
  currentBook = {},
}) => {
  if (
    currentBook.privacy === 'unpublished' ||
    !accessUtil.isAllowed(currentBook.access, ['edit'])
  ) {
    // can't add readers to unpublished books
    return false;
  }
  const maxReadersPerBook = getMaxReadersPerBook({
    activeAccount,
    userProfile,
    currentBook,
  });
  if (maxReadersPerBook === -1) {
    // this account can add as many readers as they please
    return true;
  }
  // this account has a readers per book limit. check if it's been exceeded.
  const totalInvitationsAndAccepted =
    activeReaders ??
    readers.filter(
      reader =>
        readerCapStatuses.includes(reader.invitation.status) &&
        reader.enabled === true &&
        reader.swap === undefined &&
        reader.source !== 'book'
    ).length;
  // return true if the account has not yet exceeded the amount of reader slots for this account
  return maxReadersPerBook > totalInvitationsAndAccepted;
};

const getBookCounts = (writingList = []) => {
  const unpublishedCount = writingList.filter(
    book => book?.privacy === 'unpublished'
  ).length;
  const privateCount = writingList.filter(
    book => book?.privacy === 'private'
  ).length;
  const closedCount = writingList.filter(
    book => book?.privacy === 'closed'
  ).length;
  const listedCount = writingList.filter(
    book => book?.privacy === 'listed'
  ).length;
  const publishedCount = privateCount + closedCount + listedCount;

  return {
    unpublishedCount,
    privateCount,
    closedCount,
    listedCount,
    publishedCount,
  };
};

const canEditAccount = ({ activeAccount, userProfile }) =>
  activeAccount && activeAccount.owner._id === userProfile._id;

const canInviteAccountMembers = ({ userProfile = {} }) => {
  const { account } = userProfile;
  return account && account.product && account.product.internalId !== 'free';
};

const isOnFreePlan = ({ userProfile = {} }) => {
  const { account } = userProfile;
  return account?.product?.internalId === 'free';
};

export default {
  canAddMoreReaders,
  getMaxReadersPerBook,
  getBookCounts,
  canEditAccount,
  canInviteAccountMembers,
  isOnFreePlan,
};
